import { AppBox } from '../../../../component/common/analog/molecule/AppBox'
import { observer } from 'mobx-react-lite'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { ELearningCoursePlanItem } from '../../model/ELearningCoursePlanItem'
import { Palette } from 'src/feature/theme/model/Palette'
import DeleteIcon from '@mui/icons-material/Delete'
import UpdateIcon from '@mui/icons-material/Update'
import { Tooltip, Box, Card, CardMedia, CardContent, Typography } from '@mui/material'
import { CourseProgressRow } from './CourseProgressRow'
import { useState } from 'react'
import { ModalWindow } from 'src/component/common/analog/template/ModalWindow'
import { Paragraph } from 'src/feature/localization/component/atom/Paragraph'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { useELearningStore } from '../../store/e-learning.store'
import { CoursePresentation } from './CoursePresentation'
import FavoriteIcon from '@mui/icons-material/Favorite'
import { AppDivider } from 'src/component/common/analog/molecule/AppDivider'

export type CoursePlanItemProp = {
  item: ELearningCoursePlanItem
  index: number
  managerView?: boolean
}

export const CoursePlanItem = observer(({ item, managerView }: CoursePlanItemProp) => {
  const { translation } = useLocalizationStore()
  const eLearningStore = useELearningStore()
  
  const texts = translation.pages.eLearning
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [showCourseInfo, setShowCourseInfo] = useState(false)

  const removeItem = async () => {
    await eLearningStore.removeCoursePlanItem(item.id)
    setShowConfirmDelete(false)
    await eLearningStore.getRecommendedCourses()
  }

  return (
    <>
      <Card sx={styles.card}>
        <Box sx={styles.mediaContainer} onClick={() => !managerView && setShowCourseInfo(true)}>
          <CardMedia
            component="img"
            alt={item.title}
            image={item.imageUrl}
            sx={styles.image}
          />
          <Box sx={styles.overlay} />
          {item.isUserSelected && <FavoriteIcon sx={{
            position: 'absolute',
            top: 10,
            right: 16,
            color: Palette.DANGER,
          }} fontSize='small' />}
        </Box>
        <CardContent sx={styles.contentContainer}>
          <AppBox sx={styles.actions}>
            <Typography sx={{ ...styles.text, color: 'text.primary', WebkitLineClamp: '2', height: '3em', fontWeight: 500 }} onClick={() => !managerView && setShowCourseInfo(true)}>{item.title}</Typography>
            {!managerView && item.isUserSelected && <DeleteIcon fontSize='small' sx={styles.iconButton} onClick={() => setShowConfirmDelete(true)} />}
            {item.mandatory && <Tooltip title={(item.dealine ? new Date(item.dealine) < new Date() ? texts.courseOverdue(new Date(item.dealine).toLocaleDateString()) : texts.courseMandatoryWithDeadline(new Date(item.dealine).toLocaleDateString()) : texts.courseMandatory())} arrow><UpdateIcon fontSize='small' style={{ color: Palette.DANGER }} /></Tooltip>}
          </AppBox>
          <AppBox sx={{ gap: 5, alignItems: 'center' }}>
            {/* <Typography variant='body2' sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{`${'0,0'} (${0} ratings)`}</Typography> */}
            {/* {!managerView && (item?.progress?.percentage || 0) > 50 && <Rating defaultValue={0} size="small" sx={styles.icon} />} */}
          </AppBox>
          {item.progress && item.progress?.percentage > 0 && <CourseProgressRow progress={item.progress} />}
        </CardContent>
        <ModalWindow
          modalProps={{
            open: showConfirmDelete,
            onClose: () => setShowConfirmDelete(false),
            PaperProps: {
              sx: {
                maxWidth: { xs: '90%', md: '60%', lg: '30%' },
                borderRadius: '10px',
              },
            },
          }}
          titleProps={{ textValue: texts.removeCourseText() }}
        >
          <AppBox sx={styles.dialogContent}>
            <Paragraph textValue={texts.confirmDelete(item.title)} m={5} />
            <AppDivider />
            <AppBox sx={{ gap: 10, justifyContent: 'flex-end' }}>
              <AppButton
                fontSize={12}
                textValue={translation.buttons.cancel}
                variant={'light'}
                onClick={() => setShowConfirmDelete(false)}
              />
              <AppButton
                fontSize={12}
                textValue={translation.buttons.confirm}
                variant="contained"
                onClick={removeItem}
              />
            </AppBox>
          </AppBox>
        </ModalWindow>
      </Card>
      {showCourseInfo && <ModalWindow
        modalProps={{
          open: showCourseInfo,
          onClose: () => setShowCourseInfo(false),
          PaperProps: {
            sx: {
              maxWidth: { xs: '90%', md: '70%' },
              borderRadius: '10px',
            },
          },
        }}
      >
        <CoursePresentation courseId={item.courseId} />
      </ModalWindow>}
    </>
  )
})

const styles = {
  card: {
    maxWidth: 230,
    minWidth: 230,
    cursor: 'pointer',
    borderRadius: '10px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    borderColor: `${Palette.DEEP_SEA_BLUE}30`,
  },
  mediaContainer: {
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 9', // 16:9 aspect ratio
    overflow: 'hidden',
    borderRadius: '10px',
    transition: 'transform 0.5s ease',
    '&:hover img': {
      transform: 'scale(1.15)',
    },
  },
  image: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.5s ease',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: Palette.DEEP_SEA_BLUE,
    opacity: 0.5, // 50% black overlay
  },
  contentContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: 5,
    paddingBottom: 0,
  },
  text: {
    fontSize: 12,
    display: '-webkit-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    WebkitBoxOrient: 'vertical',
  },
  iconButton: {
    color: 'text.secondary',
    transition: 'color 0.3s ease',
    '&:hover': {
      color: Palette.DANGER,
    },
  },
  icon: {
    color: 'alternate.primary',
    '&:hover': {
      opacity: 0.7,
    },
  },
  dialogContent: {
    flexDirection: 'column',
    gap: 10,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '5px',
  },
}
